/**
 * Comment renderer module.
 *
 * Purpose: Handle filtering, comparison, and mark updates for comment annotations.
 * This module manages the rendering state and applies marks based on resolved/selected status.
 *
 * Key responsibilities:
 * - Filter annotations based on status (terminal/resolved) and selected state
 * - Compare previous vs current filtered annotations to detect removals
 * - Remove marks for annotations that should no longer be shown
 * - Re-apply marks for all filtered annotations
 *
 * Dependencies:
 * - core/state.ts (for reading annotations and selected state)
 * - adapters/host/doc.ts (for finding text occurrences)
 * - adapters/host/marks.ts (for applying/removing marks)
 * - types/velt.ts (for CommentAnnotation)
 *
 * IMPORTANT: This module MUST NOT import editor or Velt types directly.
 * All SDK access goes through adapters.
 */
import { applyAnnotationMarkByText, removeAnnotationMark } from '@/adapters/host/marks';
import { Constants } from '@/constants/common';
import { getCommentAnnotations, getSelectedAnnotations } from '@/core/state';
import { Console } from '@/utils/console';
const rendererStore = new Map();
/**
 * Filters annotations based on status and selected state.
 * Excludes terminal/resolved comments unless they're selected.
 *
 * @param annotations Array of CommentAnnotation to filter
 * @param selectedIds Set of selected annotation IDs
 * @returns Filtered array of CommentAnnotation
 */
const filterAnnotations = (annotations, selectedIds) => {
    return annotations.filter((annotation) => {
        var _a;
        // Include if not terminal/resolved
        if (((_a = annotation === null || annotation === void 0 ? void 0 : annotation.status) === null || _a === void 0 ? void 0 : _a.type) !== Constants.STATUS_TERMINAL) {
            return true;
        }
        // Include terminal/resolved only if selected
        return selectedIds.has(annotation.annotationId);
    });
};
/**
 * Applies marks for filtered annotations.
 * Finds text occurrences and applies marks to the editor.
 *
 * @param editor The editor instance
 * @param filteredAnnotations Array of filtered CommentAnnotation to render
 */
const applyMarksForAnnotations = (editor, filteredAnnotations) => {
    var _a;
    for (const annotation of filteredAnnotations) {
        const textEditorConfig = (_a = annotation === null || annotation === void 0 ? void 0 : annotation.context) === null || _a === void 0 ? void 0 : _a.textEditorConfig;
        if (!(textEditorConfig === null || textEditorConfig === void 0 ? void 0 : textEditorConfig.text) || !annotation.annotationId) {
            continue;
        }
        const textToFind = textEditorConfig.text;
        const occurrence = textEditorConfig.occurrence || 1;
        const targetTextNodeId = textEditorConfig.targetTextNodeId;
        // Use applyAnnotationMarkByText which takes text and occurrence directly
        // This function handles finding the text and applying the mark internally
        applyAnnotationMarkByText(editor, textToFind, occurrence, annotation.annotationId, annotation.multiThreadAnnotationId, targetTextNodeId);
    }
};
/**
 * Updates comments for an editor by filtering, comparing, removing, and re-applying marks.
 * This is the core rendering logic that handles resolved comment filtering.
 *
 * @param editorId Unique identifier for the editor
 * @param editor The editor instance
 * @param annotations All CommentAnnotation from Velt SDK (with status)
 */
export const updateComments = (editorId, editor, annotations) => {
    var _a;
    const logPrefix = '[commentRenderer:updateComments]';
    try {
        // Store editor instance in renderer store
        const storeEntry = rendererStore.get(editorId);
        if (storeEntry) {
            storeEntry.editor = editor;
        }
        else {
            rendererStore.set(editorId, {
                editor,
                previousFilteredAnnotations: [],
            });
        }
        // Get selected annotations from state
        const selectedIds = getSelectedAnnotations(editorId);
        // Filter annotations based on status and selected state
        const filteredAnnotations = filterAnnotations(annotations, selectedIds);
        // Get previous filtered annotations for comparison
        const previousFilteredAnnotations = ((_a = rendererStore.get(editorId)) === null || _a === void 0 ? void 0 : _a.previousFilteredAnnotations) || [];
        // Create sets of annotation IDs for comparison
        const newAnnotationIds = new Set(filteredAnnotations.map((ann) => ann.annotationId).filter(Boolean));
        const previousAnnotationIds = new Set(previousFilteredAnnotations.map((ann) => ann.annotationId).filter(Boolean));
        // Find annotations that should be removed (were in previous, not in new)
        const removedAnnotationIds = Array.from(previousAnnotationIds).filter((id) => !newAnnotationIds.has(id));
        // Remove marks for removed annotations
        for (const annotationId of removedAnnotationIds) {
            if (annotationId) {
                removeAnnotationMark(editor, annotationId);
            }
        }
        // Update previous filtered annotations in renderer store
        const currentStoreEntry = rendererStore.get(editorId);
        if (currentStoreEntry) {
            currentStoreEntry.previousFilteredAnnotations = filteredAnnotations;
        }
        // Re-apply marks for all filtered annotations
        // This ensures newly selected resolved comments are highlighted
        applyMarksForAnnotations(editor, filteredAnnotations);
    }
    catch (error) {
        Console.catch(`${logPrefix} Error updating comments:`, error);
    }
};
/**
 * Updates comments when selected annotations change.
 * Re-reads annotations from state and triggers re-rendering.
 *
 * @param editorId Unique identifier for the editor
 * @param selectedIds Set of selected annotation IDs (already updated in state)
 */
export const updateSelection = (editorId, selectedIds) => {
    const logPrefix = '[commentRenderer:updateSelection]';
    try {
        // Get editor instance from renderer store
        const storeEntry = rendererStore.get(editorId);
        if (!storeEntry || !storeEntry.editor) {
            Console.warn(`${logPrefix} No editor found for editorId: ${editorId}`);
            return;
        }
        // Get all comment annotations from state (with status)
        const annotations = getCommentAnnotations(editorId);
        // Re-run updateComments with current annotations and new selection
        // This will filter, compare, remove, and re-apply marks
        updateComments(editorId, storeEntry.editor, annotations);
    }
    catch (error) {
        Console.catch(`${logPrefix} Error updating selection:`, error);
    }
};
/**
 * Cleans up renderer store for an editor.
 * Should be called when editor is destroyed.
 *
 * @param editorId Unique identifier for the editor
 */
export const cleanupRenderer = (editorId) => {
    rendererStore.delete(editorId);
};
